import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowLeft, RefreshCw, Search, CheckCircle, XCircle, Users, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl } from '../lib/assetUrls';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
    return { Authorization: `Bearer ${getToken()}` };
}

const POSITION_COLORS = {
    Goalkeeper: 'bg-yellow-500/20 text-yellow-300',
    Defender:   'bg-blue-500/20 text-blue-300',
    Midfielder: 'bg-green-500/20 text-green-300',
    Forward:    'bg-red-500/20 text-red-300',
};

function PosBadge({ pos }) {
    if (!pos) return null;
    const cls = POSITION_COLORS[pos] || 'bg-muted text-muted-foreground';
    return (
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cls}`}>
            {pos.slice(0, 3).toUpperCase()}
        </span>
    );
}

function TeamBadge({ teamId, size = 22 }) {
    if (!teamId) return null;
    const numId = teamId.startsWith('t') ? teamId.slice(1) : teamId;
    return (
        <img
            src={`https://secure.cache.images.core.optasports.com/soccer/teams/150x150/${numId}.png`}
            alt=""
            width={size}
            height={size}
            className="rounded-sm object-contain flex-shrink-0"
            onError={e => { e.target.style.display = 'none'; }}
        />
    );
}

// ── Search modal ──────────────────────────────────────────────────────────────

function MapPlayerModal({ player, onClose, onMapped }) {
    const [query, setQuery] = useState(player.name);
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(null); // statsapi_player_id being saved
    const debounceRef = useRef(null);

    // Auto-search on open with player name
    useEffect(() => {
        doSearch(player.name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function doSearch(q) {
        if (!q || q.trim().length < 2) { setResults([]); return; }
        setSearching(true);
        axios.get(`${API_URL}/api/admin/football/worldcup/player-search`, {
            params: { q },
            headers: authHeaders(),
        }).then(res => {
            setResults(res.data.players || []);
        }).catch(() => {
            toast.error('Search failed');
        }).finally(() => {
            setSearching(false);
        });
    }

    function handleQueryChange(e) {
        const val = e.target.value;
        setQuery(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(val), 400);
    }

    async function confirmMapping(statsapiPlayer) {
        setSaving(statsapiPlayer.id);
        try {
            await axios.post(
                `${API_URL}/api/admin/football/worldcup/save-player-mapping`,
                {
                    pa_player_id:       player.pa_player_id,
                    pa_name:            player.name,
                    pa_team_id:         player.pa_team_id,
                    jersey:             player.shirt,
                    statsapi_player_id: statsapiPlayer.id,
                    statsapi_name:      statsapiPlayer.name,
                },
                { headers: authHeaders() }
            );
            toast.success(`Mapped ${player.name} → ${statsapiPlayer.name}`);
            onMapped(player.pa_player_id);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(null);
        }
    }

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <TeamBadge teamId={player.pa_team_id} size={20} />
                        <span>Map: {player.name}</span>
                        {player.shirt && (
                            <span className="text-xs text-muted-foreground font-normal">#{player.shirt}</span>
                        )}
                        <PosBadge pos={player.position} />
                    </DialogTitle>
                </DialogHeader>

                {/* Search input */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        placeholder="Search StatsAPI by name..."
                        value={query}
                        onChange={handleQueryChange}
                        autoFocus
                    />
                    {searching && (
                        <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    )}
                </div>

                {/* Results */}
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                    {results.length === 0 && !searching && query.length >= 2 && (
                        <p className="text-center text-sm text-muted-foreground py-6">No results found</p>
                    )}
                    {results.map(candidate => {
                        const club = candidate.current_team?.name || '';
                        const nat  = candidate.nationality || '';
                        const pos  = candidate.position || '';
                        return (
                            <div
                                key={candidate.id}
                                className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2.5 hover:bg-muted/50 transition-colors"
                            >
                                <div className="min-w-0">
                                    <p className="font-medium text-sm truncate">{candidate.name}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {[nat, club, pos].filter(Boolean).join(' · ')}
                                        {candidate.current_team?.jersey_number && (
                                            <span className="ml-1 text-muted-foreground/70">
                                                #{candidate.current_team.jersey_number}
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <Button
                                    size="sm"
                                    className="flex-shrink-0 gap-1"
                                    disabled={!!saving}
                                    onClick={() => confirmMapping(candidate)}
                                >
                                    {saving === candidate.id
                                        ? <RefreshCw className="h-3 w-3 animate-spin" />
                                        : <CheckCircle className="h-3 w-3" />}
                                    Select
                                </Button>
                            </div>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminWCPlayerMappingsPage() {
    const [unmapped, setUnmapped] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [teamFilter, setTeamFilter] = useState('');
    const [mappingPlayer, setMappingPlayer] = useState(null);
    const [collapsedTeams, setCollapsedTeams] = useState(new Set());
    const [seeding, setSeeding] = useState(false);
    const [seedProgress, setSeedProgress] = useState(null); // {progress, total}
    const [seedResult, setSeedResult] = useState(null);
    const pollRef = useRef(null);

    const fetchUnmapped = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/admin/football/worldcup/unmapped-players`,
                { headers: authHeaders() }
            );
            setUnmapped(res.data.unmapped || []);
        } catch {
            toast.error('Failed to load unmapped players');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchUnmapped(); }, [fetchUnmapped]);

    async function handleSeed() {
        setSeeding(true);
        setSeedResult(null);
        setSeedProgress(null);
        clearInterval(pollRef.current);
        try {
            // Kick off background task — returns immediately with task_id
            const kickoff = await axios.post(
                `${API_URL}/api/admin/football/worldcup/seed-player-mappings`,
                {},
                { headers: authHeaders() }
            );
            const { task_id, total } = kickoff.data;
            setSeedProgress({ progress: 0, total });

            // Poll for completion every 2s
            pollRef.current = setInterval(async () => {
                try {
                    const status = await axios.get(
                        `${API_URL}/api/admin/football/worldcup/seed-player-mappings/${task_id}`,
                        { headers: authHeaders() }
                    );
                    const data = status.data;
                    if (data.status === 'running') {
                        setSeedProgress({ progress: data.progress, total: data.total });
                    } else if (data.status === 'done') {
                        clearInterval(pollRef.current);
                        setSeedResult(data);
                        setSeedProgress(null);
                        setSeeding(false);
                        const skipped = data.skipped_no_statsapi_id || 0;
                        toast.success(`Done — ${data.mapped} mapped, ${skipped} not in StatsAPI, ${data.failed} failed`);
                        fetchUnmapped();
                    }
                } catch {
                    clearInterval(pollRef.current);
                    setSeeding(false);
                    toast.error('Failed to get task status');
                }
            }, 2000);
        } catch (e) {
            setSeeding(false);
            toast.error('Seed failed: ' + (e.response?.data?.detail || e.message));
        }
    }

    function handleMapped(pa_player_id) {
        setUnmapped(prev => prev.filter(p => p.pa_player_id !== pa_player_id));
    }

    function toggleTeam(teamId) {
        setCollapsedTeams(prev => {
            const next = new Set(prev);
            if (next.has(teamId)) next.delete(teamId);
            else next.add(teamId);
            return next;
        });
    }

    // Group by team
    const teamGroups = [];
    const seen = new Set();
    for (const p of unmapped) {
        if (!seen.has(p.pa_team_id)) {
            seen.add(p.pa_team_id);
            teamGroups.push({
                pa_team_id:   p.pa_team_id,
                pa_team_name: p.pa_team_name,
            });
        }
    }
    teamGroups.sort((a, b) => a.pa_team_name.localeCompare(b.pa_team_name));

    const lowerFilter = filter.toLowerCase();
    const visibleGroups = teamGroups
        .filter(t => !teamFilter || t.pa_team_id === teamFilter)
        .map(t => ({
            ...t,
            players: unmapped.filter(p =>
                p.pa_team_id === t.pa_team_id &&
                (!lowerFilter || p.name.toLowerCase().includes(lowerFilter))
            ),
        }))
        .filter(t => t.players.length > 0);

    return (
        <div className="container max-w-3xl mx-auto py-6 px-4 space-y-5">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Link to="/admin/football">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                </Link>
                <div>
                    <h1 className="text-2xl font-bold page-title">WC Player Mappings</h1>
                    <p className="text-sm text-muted-foreground">
                        Map unmapped WC squad players to their StatsAPI IDs
                    </p>
                </div>
                <div className="ml-auto flex gap-2">
                    <Button
                        size="sm"
                        className="gap-2"
                        onClick={handleSeed}
                        disabled={seeding || loading}
                    >
                        {seeding
                            ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            : <CheckCircle className="h-3.5 w-3.5" />}
                        {seeding
                            ? seedProgress
                                ? `Mapping… ${seedProgress.progress}/${seedProgress.total}`
                                : 'Starting…'
                            : 'Auto-map Known Players'}
                    </Button>
                    <Button variant="outline" size="sm" className="gap-2" onClick={fetchUnmapped} disabled={loading}>
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Seed result banner */}
            {seedResult && (
                <div className="rounded-lg border bg-card p-4 space-y-3">
                    <div className="flex items-center gap-4 flex-wrap">
                        <span className="text-sm font-semibold">Seed result:</span>
                        <span className="text-sm text-emerald-400">✓ {seedResult.mapped} mapped</span>
                        {(seedResult.skipped_no_statsapi_id || 0) > 0 && (
                            <span className="text-sm text-muted-foreground">↷ {seedResult.skipped_no_statsapi_id} not in StatsAPI</span>
                        )}
                        {seedResult.failed > 0 && (
                            <span className="text-sm text-red-400">✗ {seedResult.failed} failed</span>
                        )}
                    </div>
                    {seedResult.details?.skipped?.length > 0 && (
                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground font-medium">Not in StatsAPI database (map manually if needed):</p>
                            {seedResult.details.skipped.map((p, i) => (
                                <p key={i} className="text-xs text-muted-foreground">
                                    <span className="text-foreground">{p.pa_name}</span> ({p.team_name})
                                </p>
                            ))}
                        </div>
                    )}
                    {seedResult.details?.failed?.length > 0 && (
                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground font-medium">Failed (use manual Map button):</p>
                            {seedResult.details.failed.map((p, i) => (
                                <p key={i} className="text-xs text-muted-foreground">
                                    <span className="text-foreground">{p.pa_name}</span> — {p.reason}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Summary */}
            {!loading && (
                <div className="flex gap-3">
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-2xl font-bold text-red-400">{unmapped.length}</p>
                        <p className="text-xs text-muted-foreground">Unmapped Players</p>
                    </div>
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-2xl font-bold text-amber-400">{teamGroups.length}</p>
                        <p className="text-xs text-muted-foreground">Teams Affected</p>
                    </div>
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-2xl font-bold text-emerald-400">{visibleGroups.length}</p>
                        <p className="text-xs text-muted-foreground">Teams Shown</p>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        placeholder="Filter by player name..."
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                    />
                </div>
                <select
                    className="rounded-md border bg-card text-sm px-3 py-2 text-foreground min-w-[160px]"
                    value={teamFilter}
                    onChange={e => setTeamFilter(e.target.value)}
                >
                    <option value="">All teams</option>
                    {teamGroups.map(t => (
                        <option key={t.pa_team_id} value={t.pa_team_id}>
                            {t.pa_team_name} ({unmapped.filter(p => p.pa_team_id === t.pa_team_id).length})
                        </option>
                    ))}
                </select>
            </div>

            {/* Loading skeleton */}
            {loading && (
                <div className="space-y-3">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
            )}

            {/* Empty state */}
            {!loading && unmapped.length === 0 && (
                <div className="rounded-lg border bg-card py-16 text-center text-muted-foreground">
                    <CheckCircle className="h-10 w-10 mx-auto mb-3 text-emerald-500 opacity-60" />
                    <p className="font-medium">All players mapped!</p>
                    <p className="text-sm mt-1">Run "Map All WC Players" from the Football admin page to auto-map first.</p>
                </div>
            )}

            {/* Player groups by team */}
            {!loading && visibleGroups.map(group => {
                const collapsed = collapsedTeams.has(group.pa_team_id);
                return (
                    <div key={group.pa_team_id} className="rounded-lg border bg-card overflow-hidden">
                        {/* Team header */}
                        <button
                            className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-muted/40 transition-colors"
                            onClick={() => toggleTeam(group.pa_team_id)}
                        >
                            <TeamBadge teamId={group.pa_team_id} />
                            <span className="font-semibold text-sm flex-1 text-left">{group.pa_team_name}</span>
                            <span className="text-xs text-muted-foreground">{group.players.length} unmapped</span>
                            {collapsed
                                ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                : <ChevronUp className="h-4 w-4 text-muted-foreground" />}
                        </button>

                        {/* Player rows */}
                        {!collapsed && (
                            <div className="divide-y divide-border">
                                {group.players.map(player => (
                                    <div
                                        key={player.pa_player_id}
                                        className="flex items-center gap-3 px-4 py-2.5"
                                    >
                                        <span className="text-xs text-muted-foreground w-6 text-right flex-shrink-0">
                                            {player.shirt || '–'}
                                        </span>
                                        <span className="flex-1 text-sm font-medium">{player.name}</span>
                                        <PosBadge pos={player.position} />
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1.5 h-7 text-xs"
                                            onClick={() => setMappingPlayer(player)}
                                        >
                                            <Users className="h-3 w-3" />
                                            Map
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {!loading && visibleGroups.length === 0 && unmapped.length > 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">No players match your filter.</p>
            )}

            {/* Map modal */}
            {mappingPlayer && (
                <MapPlayerModal
                    player={mappingPlayer}
                    onClose={() => setMappingPlayer(null)}
                    onMapped={handleMapped}
                />
            )}
        </div>
    );
}
